import { observable, computed, action } from 'mobx';
import { computedFn } from 'mobx-utils';
import {
  RootStore, EDITOR_LOCATIONS_STORE, ALL_LOCATIONS, UNGROUPED_SHOTS, EDITOR_SHOTS_STORE
} from 'store';
import { Group } from './types';

export const EDITOR_GROUPS_STORE = 'EDITOR_GROUPS_STORE';

export class EditorGroupsStore {
  @observable groups: { [key: string]: Group } = {};
  @observable filterGroupsBySelectedLocation: boolean = false;
  @observable selectedGroups: string[] = [];

  rootStore: RootStore;
  counter: number;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.counter = 0;
  }

  private getNewGroupName() {
    this.counter++;
    let newGroupName = `unnamed group ${this.counter}`;

    while (this.groups.hasOwnProperty(newGroupName)) {
      this.counter++;
      newGroupName = `unnamed group ${this.counter}`;
    }

    return newGroupName;
  }

  @computed get sortedGroupsByPreviewFrame() {
    return Object.values(this.groups)
      .sort((a: any, b: any) => a.previewFrame - b.previewFrame);
  }

  @computed get isAnyGroupSelected() {
    return !!this.selectedGroups.length;
  }

  @computed get sortedGroupsFilteredBySelectedLocation() {
    const {
      [EDITOR_LOCATIONS_STORE]: { selectedLocation, shotIdsForSelectedLocation }
    } = this.rootStore;
    if (!this.filterGroupsBySelectedLocation ||
      !selectedLocation ||
      selectedLocation === ALL_LOCATIONS
    ) return this.sortedGroupsByPreviewFrame;

    return this.sortedGroupsByPreviewFrame
      .filter((group: any) =>
        group.shotIds.some(id => shotIdsForSelectedLocation.includes(id))
      );
  }

  @computed get groupNames() {
    return Object.keys(this.groups);
  }

  @computed get sortedGroupsList() {
    return Object.values(this.groups)
      .sort((a: any, b: any) => a.previewFrame - b.previewFrame);
  }

  @observable isGroupSelected = computedFn(groupName => {
    return this.selectedGroups.includes(groupName);
  });

  @observable getGroupByName = computedFn(groupName => {
    if (this.groups[groupName]) {
      return { ...this.groups[groupName], name: groupName };
    }

    return {
      shotIds: [],
      previewFrame: null,
      name: groupName
    };
  });

  @observable relatedProjectsPerGroup = computedFn(groupName => {
    if (!groupName) return [];

    const {
      [EDITOR_SHOTS_STORE]: { getShotById }
    } = this.rootStore;

    const { shotIds } = this.getGroupByName(groupName);
    const relatedMediaPerGroup = [];

    const relatedMediaPerShots = shotIds.map(id => getShotById(id).relatedProjects);

    relatedMediaPerShots
      .forEach(mediaPerOneShot => {
        mediaPerOneShot
          .forEach(({ id }) => {
            const alreadyAdded = !!relatedMediaPerGroup.find(obj => obj.id === id);

            if (!alreadyAdded) {
              let shouldAdd = true;

              relatedMediaPerShots
                .forEach(mediaArr => {
                  if (!shouldAdd) return;
                  const index = mediaArr.findIndex(({ id: projectId }) => projectId === id);
                  if (index < 0) {
                    shouldAdd = false;
                  }
                });

              shouldAdd && relatedMediaPerGroup.push({ id });
            }
          });
      });

    return relatedMediaPerGroup;
  });

  @action.bound clearEditorGroupsState() {
    this.setGroups();
    this.setSelectedGroups();
    this.setFilterGroupsBySelectedLocation();
  }

  @action.bound setGroups(catalog = {}) {
    this.groups = catalog;
  }

  @action.bound setSelectedGroups(list = []) {
    this.selectedGroups = list;
  }

  @action.bound setFilterGroupsBySelectedLocation(value = false) {
    if (value === this.filterGroupsBySelectedLocation) return;
    this.filterGroupsBySelectedLocation = value;
    this.setSelectedGroups();
  }

  @action.bound switchGroupSelection(groupName, singleSelection = false) {
    const index = this.selectedGroups.findIndex(g => g === groupName);
    const isGroupSelected = index > -1;

    singleSelection
      ? isGroupSelected
        ? this.setSelectedGroups()
        : this.setSelectedGroups([groupName])
      : isGroupSelected
        ? this.selectedGroups.splice(index, 1)
        : this.selectedGroups.push(groupName);
  }

  @action.bound switchAllGroupSelection(selected = false) {
    selected
      ? this.setSelectedGroups()
      : this.setSelectedGroups(this.groupNames);
  }

  @action.bound removeShotFromGroup(groupName, shotId) {
    const group = this.getGroupByName(groupName);

    group.shotIds = group.shotIds.filter(id => id.toString() !== shotId.toString());

    if (group.shotIds.length) {
      const shotStartFrames = group.shotIds
        .map(id => this.rootStore[EDITOR_SHOTS_STORE].getShotById(id).startFrame);
      group.previewFrame = Math.min(...shotStartFrames);
      this.groups[groupName] = group;
    } else {
      delete this.groups[groupName];
      this.selectedGroups = [...this.selectedGroups].filter(g => g !== groupName);
    }
  }

  @action.bound addShotToGroup(shot) {
    const {
      id: shotId,
      name,
      startFrame
    } = shot;
    const groupName = name || UNGROUPED_SHOTS;
    const group = this.getGroupByName(groupName);
    group.shotIds.push(shotId);
    group.previewFrame = group.previewFrame
      ? Math.min(group.previewFrame, startFrame)
      : startFrame;
    this.groups[groupName] = group;
  }

  @action.bound createNewGroup(shotId) {
    const {
      [EDITOR_SHOTS_STORE]: { getShotById, updateShot }
    } = this.rootStore;
    const groupName = this.getNewGroupName();
    const shot = { ...getShotById(shotId), name: groupName };

    updateShot(shot);
  }

  @action.bound renameGroup(groupName, newGroupName) {
    newGroupName = newGroupName.trim();

    if (groupName === newGroupName) return;

    const {
      [EDITOR_SHOTS_STORE]: { getShotById, updateShots }
    } = this.rootStore;

    const group = this.getGroupByName(groupName);
    const shots = group.shotIds
      .map(id => ({ ...getShotById(id), name: newGroupName }));

    updateShots(shots);
  }

  @action.bound combineSelectedGroups() {
    const {
      [EDITOR_SHOTS_STORE]: { getShotById, updateShots }
    } = this.rootStore;
    const name = this.getNewGroupName();
    const shots = [];
    this.selectedGroups.forEach(groupName => {
      this.getGroupByName(groupName).shotIds
        .forEach(shotId => shots.push({ ...getShotById(shotId), name }));
    });

    updateShots(shots);
  }

  @action.bound deleteSelectedGroups() {
    const {
      [EDITOR_SHOTS_STORE]: { getShotById, updateShots }
    } = this.rootStore;
    const shots = [];
    this.selectedGroups
      .forEach(groupName => {
        this.getGroupByName(groupName).shotIds
          .forEach(shotId => shots.push({ ...getShotById(shotId), name: null }));
      });
    updateShots(shots);
  }

  @action.bound clearSelectedGroups() {
    this.setSelectedGroups();
  }

  @action.bound updateSelectedGroupRelatedProjects(projectsToRemove, projectsToAdd) {
    const {
      [EDITOR_SHOTS_STORE]: { getShotById, updateShots }
    } = this.rootStore;

    const shotsToUpdate = [];
    this.getGroupByName(this.selectedGroups[0]).shotIds
      .forEach(shotId => {
        const cloneShot = { ...getShotById(shotId) };
        cloneShot.relatedProjects = cloneShot.relatedProjects.filter(p => {
          return !projectsToRemove.find(project => project.id === p.id);
        });

        const filteredProjectsToAdd = projectsToAdd.filter(p => {
          return !cloneShot.relatedProjects.find(project => project.id === p.id);
        });

        cloneShot.relatedProjects = [...cloneShot.relatedProjects, ...filteredProjectsToAdd];
        shotsToUpdate.push(cloneShot);
      });

    updateShots(shotsToUpdate);
  }
}
